import { format, startOfYear } from 'date-fns';
import { BsHouseFill } from 'react-icons/bs';
import Select from 'react-select';

import { BaseKycFlags, Note, toOptions } from '@qwealth/qdata';
import { Box } from '@qwealth/qcore';

import { BASE_DATE_FORMAT, formatDate } from './dateTimeHelper';
import {
  ClientComplianceReview,
  ClientComplianceReviewFilters,
  COMPLIANCE_FILTERS_KEY,
  ComplianceChecklistActor,
  ComplianceChecklistStatus,
} from 'data/models/Compliance';
import { caseInsensitiveFuzzySearch } from 'utils/search';
import NavigationLink from 'components/common/NavigationLink';
import InfoTag from 'components/common/SectionHeader/InfoTag';
import { changeClientComplianceReviewAssignee } from 'data/actions/compliance';
import { AppThunkDispatch } from '../store';
import { baseSelectStyles } from 'components/common/BaseSelect';

export const hasKycFlagChecked = (model: BaseKycFlags | Note): boolean => {
  const hasKycFlag = model.isMeaningfulInteraction === 1 || model.isSignificantChange === 1;

  // only StickyNote doesn't have isPortfolioReview field
  if ('Creator' in model) {
    return hasKycFlag;
  }

  return hasKycFlag || (model as BaseKycFlags).isPortfolioReview === 1;
};

export const initialFilters: ClientComplianceReviewFilters = {
  partnerName: null,
  complianceChecklistStatus: null,
  ipsDateStart: format(startOfYear(new Date()), BASE_DATE_FORMAT),
  ipsDateEnd: format(new Date(), BASE_DATE_FORMAT),
  assignee: null,
  responsibleComplianceActors: null,
};

export const getSavedFilters = (): ClientComplianceReviewFilters => {
  const complianceFilters = localStorage.getItem(COMPLIANCE_FILTERS_KEY);
  if (complianceFilters) {
    return JSON.parse(complianceFilters);
  }

  return initialFilters;
};

export const search = (clientToReview: ClientComplianceReview, query: string): boolean =>
  caseInsensitiveFuzzySearch(query, [
    clientToReview.QID,
    clientToReview.name,
    clientToReview.household.QID,
    clientToReview.household.name,
    clientToReview.assignee,
    clientToReview.iaCode,
  ]);

export const filteredClientsToReview = (clientsToReview: ClientComplianceReview[], query: string) =>
  clientsToReview.filter(clientToReview => {
    return search(clientToReview, query);
  });

export const saveFiltersInLocalStorage = (filters: ClientComplianceReviewFilters) => {
  localStorage.setItem(COMPLIANCE_FILTERS_KEY, JSON.stringify(filters));
};

const householdNameFormatter = (cell: string, row: ClientComplianceReview) => {
  const linkHouseholdContent = {
    label: cell,
    link: `/household?household=${row.household.QID}`,
  };
  return <NavigationLink content={linkHouseholdContent} icon={<BsHouseFill size="14px" />} fontSize="small" />;
};

const checklistStatusFormatter = (cell: ComplianceChecklistStatus) => {
  return <InfoTag tagTitle={cell} fontSize="small" />;
};

const actionRequiredByFormatter = (cell: ComplianceChecklistActor[] | null, row: ClientComplianceReview) => {
  if (!cell) return null;
  return (
    <Box display="flex" flexDirection="row" gap="small" flexWrap="wrap">
      {cell.map(actor => (
        <InfoTag key={`${row.QID}${actor}`} tagTitle={actor} fontSize="small" />
      ))}
    </Box>
  );
};

const createEditorRenderer = (cell: string | null, row: ClientComplianceReview, rowIndex, extraData) => (
  <Select
    placeholder="Select Team Member"
    styles={baseSelectStyles()}
    value={cell ? toOptions<string>([cell]) : null}
    onChange={async options => {
      await extraData.dispatch(changeClientComplianceReviewAssignee(row.QID, options ? options.value : null));
    }}
    options={toOptions<string>(extraData.complianceAssignees)}
    isClearable
    isDisabled={!extraData.hasPermissionToChangeAssignee}
  />
);

const ipsAcceptanceDateFormatter = (cell: string | null) => {
  if (!cell) return null;
  return formatDate(cell, BASE_DATE_FORMAT);
};

export const createColumns = (dispatch: AppThunkDispatch, complianceAssignees: string[], hasPermissionToChangeAssignee: boolean) => [
  {
    text: 'Household QID',
    dataField: 'household.QID',
    hidden: true,
    editable: false,
  },
  {
    text: 'Household',
    dataField: 'household.name',
    sort: true,
    editable: false,
    formatter: householdNameFormatter,
    events: {
      onClick: e => {
        e.stopPropagation();
      },
    },
  },
  {
    text: 'QID',
    dataField: 'QID',
    editable: false,
    hidden: true,
  },
  {
    text: 'Client',
    dataField: 'name',
    sort: true,
    editable: false,
  },
  {
    text: 'Partner',
    dataField: 'partnerName',
    sort: true,
    editable: false,
  },
  {
    text: 'IA Code',
    dataField: 'iaCode',
    sort: true,
    editable: false,
  },
  {
    text: 'IPS Date',
    dataField: 'ipsAcceptanceDate',
    sort: true,
    editable: false,
    formatter: ipsAcceptanceDateFormatter,
  },
  {
    text: 'Checklist Status',
    dataField: 'complianceChecklistStatus',
    sort: true,
    editable: false,
    formatter: checklistStatusFormatter,
  },
  {
    text: 'Action Required By',
    dataField: 'responsibleComplianceActors',
    sort: true,
    editable: false,
    formatter: actionRequiredByFormatter,
  },
  {
    text: 'RTQ Level',
    dataField: 'rtqLevel',
    hidden: true,
    editable: false,
  },
  {
    text: 'KYC ID',
    dataField: 'kycId',
    hidden: true,
    editable: false,
  },
  {
    text: 'Reviewer',
    dataField: 'assignee',
    sort: true,
    formatter: createEditorRenderer,
    formatExtraData: { dispatch, complianceAssignees, hasPermissionToChangeAssignee },
    events: {
      onClick: e => {
        e.stopPropagation();
      },
    },
  },
];
